import React, { useState, useEffect } from 'react';
import { quoteApi, customerApi, vehicleApi, formatApiError } from '../services/api';
import {
  FileText, Plus, Eye, FileArrowDown, Trash, Check, X, Funnel, ArrowRight, PencilSimple, ClockCounterClockwise
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import QuickCustomerCreate from '../components/QuickCustomerCreate';
import RevisionHistory from '../components/RevisionHistory';
import CustomerOverrideFields from '../components/CustomerOverrideFields';

const STATUS_LABELS = { offen: 'Offen', angenommen: 'Angenommen', abgelehnt: 'Abgelehnt' };
const STATUS_STYLES = { offen: 'badge-warning', angenommen: 'badge-success', abgelehnt: 'badge-error' };

const Quotes = () => {
  const [quotes, setQuotes] = useState([]);
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [showQuickCustomer, setShowQuickCustomer] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editingQuote, setEditingQuote] = useState(null);
  const [historyQuote, setHistoryQuote] = useState(null);

  // Form
  const [customerId, setCustomerId] = useState('');
  const [vehicleId, setVehicleId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [lineItems, setLineItems] = useState([{ description: '', quantity: 1, unit_price: 0 }]);
  const [discount, setDiscount] = useState(0);
  const [validUntil, setValidUntil] = useState('');
  const [notes, setNotes] = useState('');
  const [customerOverride, setCustomerOverride] = useState(null);

  useEffect(() => { loadData(); }, [filterStatus]);

  const loadData = async () => {
    try {
      const [qRes, cRes, vRes] = await Promise.all([
        quoteApi.getAll(filterStatus !== 'all' ? { status: filterStatus } : {}),
        customerApi.getAll(),
        vehicleApi.getAll()
      ]);
      setQuotes(qRes.data);
      setCustomers(cRes.data);
      setVehicles(vRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const resetForm = () => {
    setCustomerId(''); setVehicleId(''); setStartDate(''); setEndDate('');
    setLineItems([{ description: '', quantity: 1, unit_price: 0 }]);
    setDiscount(0); setValidUntil(''); setNotes('');
  };

  const addLine = () => setLineItems([...lineItems, { description: '', quantity: 1, unit_price: 0 }]);
  const removeLine = (i) => setLineItems(lineItems.filter((_, idx) => idx !== i));
  const updateLine = (i, f, v) => { const u = [...lineItems]; u[i] = { ...u[i], [f]: v }; setLineItems(u); };

  const handleCreate = async () => {
    if (!customerId) return;
    const validItems = lineItems.filter(it => it.description && it.unit_price > 0);
    try {
      await quoteApi.create({
        customer_id: customerId,
        vehicle_id: vehicleId || null,
        start_date: startDate || null,
        end_date: endDate || null,
        line_items: validItems,
        discount,
        valid_until: validUntil || null,
        notes: notes || null
      });
      toast.success('Angebot erstellt');
      setShowCreate(false);
      resetForm();
      loadData();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleAccept = async (id) => {
    try { await quoteApi.accept(id); toast.success('Angebot angenommen'); loadData(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const handleReject = async (id) => {
    try { await quoteApi.reject(id); toast.success('Angebot abgelehnt'); loadData(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Angebot löschen?')) return;
    try { await quoteApi.delete(id); toast.success('Angebot gelöscht'); loadData(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const openEdit = (q) => {
    setEditingQuote(q);
    setCustomerId(q.customer_id || '');
    setVehicleId(q.vehicle_id || 'none');
    setStartDate(q.start_date || '');
    setEndDate(q.end_date || '');
    // Build line items from existing (excluding the auto-generated vehicle line if vehicle_id present)
    const items = (q.line_items || [])
      .filter(li => !(q.vehicle_id && li.description?.startsWith('Fahrzeugmiete:')))
      .map(li => ({ description: li.description, quantity: li.quantity || 1, unit_price: li.unit_price || 0 }));
    setLineItems(items.length ? items : [{ description: '', quantity: 1, unit_price: 0 }]);
    setDiscount(q.discount || 0);
    setValidUntil(q.valid_until || '');
    setNotes(q.notes || '');
    setCustomerOverride(q.customer_override || null);
    setShowEdit(true);
  };

  const handleSaveEdit = async () => {
    if (!editingQuote) return;
    // Rebuild full line items list – include vehicle rental line if vehicle set
    const validItems = lineItems.filter(it => it.description && it.unit_price > 0);
    const full = [...validItems];
    if (vehicleId && vehicleId !== 'none' && startDate && endDate) {
      const vh = vehicles.find(v => v.id === vehicleId);
      if (vh) {
        const days = Math.max(1, Math.ceil((new Date(endDate) - new Date(startDate)) / 86400000));
        full.unshift({
          description: `Fahrzeugmiete: ${vh.brand} ${vh.model} (${vh.license_plate})`,
          quantity: days,
          unit_price: vh.daily_rate,
        });
      }
    }
    try {
      await quoteApi.update(editingQuote.id, {
        customer_id: customerId,
        vehicle_id: vehicleId && vehicleId !== 'none' ? vehicleId : null,
        start_date: startDate || null,
        end_date: endDate || null,
        line_items: full,
        discount,
        valid_until: validUntil || null,
        notes: notes || null,
        customer_override: customerOverride || {},
      });
      toast.success('Angebot aktualisiert');
      setShowEdit(false);
      setEditingQuote(null);
      resetForm();
      loadData();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleConvert = async (q) => {
    if (!q.vehicle_id || !q.start_date || !q.end_date) {
      toast.error('Angebot enthält kein Fahrzeug oder keine Mietdaten — Umwandlung nicht möglich');
      return;
    }
    if (!window.confirm(`Angebot ${q.quote_number} in Buchung umwandeln?`)) return;
    try {
      const res = await quoteApi.convertToBooking(q.id);
      toast.success('Buchung erstellt!');
      navigate(`/bookings/${res.data.booking_id}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadPdf = async (q) => {
    try {
      const res = await quoteApi.getPdf(q.id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `angebot_${q.quote_number}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Estimate
  const selectedVehicle = vehicles.find(v => v.id === vehicleId);
  const vehicleDays = startDate && endDate ? Math.max(1, Math.ceil((new Date(endDate) - new Date(startDate)) / 86400000)) : 0;
  const vehicleTotal = selectedVehicle && vehicleDays > 0 ? vehicleDays * selectedVehicle.daily_rate : 0;
  const itemsTotal = lineItems.reduce((s, it) => s + (it.quantity * it.unit_price), 0);
  const estTotal = vehicleTotal + itemsTotal - discount;

  return (
    <div className="p-6 space-y-6" data-testid="quotes-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-[#111418]">Angebote</h1>
          <p className="text-[#525866] mt-1">{quotes.length} Angebote</p>
        </div>
        <Button onClick={() => { resetForm(); setShowCreate(true); }} className="btn-primary" data-testid="create-quote-btn">
          <Plus weight="bold" className="mr-2" size={18} /> Angebot erstellen
        </Button>
      </div>

      <Select value={filterStatus} onValueChange={setFilterStatus}>
        <SelectTrigger className="w-48 input-swiss"><Funnel className="mr-2" size={16} /><SelectValue placeholder="Alle Status" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Alle Status</SelectItem>
          {Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
        </SelectContent>
      </Select>

      {loading ? (
        <div className="space-y-4">{[...Array(3)].map((_, i) => <div key={i} className="card-swiss h-20 skeleton" />)}</div>
      ) : quotes.length === 0 ? (
        <div className="card-swiss p-12 text-center">
          <FileText weight="thin" size={64} className="mx-auto text-[#8A94A6] mb-4" />
          <p className="text-[#525866]">Keine Angebote vorhanden</p>
        </div>
      ) : (
        <div className="card-swiss overflow-hidden">
          <table className="table-swiss">
            <thead><tr><th>Angebot</th><th>Kunde</th><th>Betrag</th><th>Gültig bis</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {quotes.map(q => (
                <tr key={q.id} data-testid={`quote-row-${q.id}`}>
                  <td><p className="font-semibold text-[#111418]">{q.quote_number}</p><p className="text-xs text-[#8A94A6]">{new Date(q.created_at).toLocaleDateString('de-DE')}</p></td>
                  <td>{q.customer?.first_name} {q.customer?.last_name}</td>
                  <td className="font-bold">€{q.total.toFixed(2)}</td>
                  <td className="text-sm">{q.valid_until ? new Date(q.valid_until).toLocaleDateString('de-DE') : '—'}</td>
                  <td><span className={`badge ${STATUS_STYLES[q.status]}`}>{STATUS_LABELS[q.status]}</span></td>
                  <td>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => downloadPdf(q)} title="PDF"><FileArrowDown weight="bold" size={16} /></Button>
                      <Button variant="ghost" size="sm" onClick={() => openEdit(q)} className="text-blue-600" title="Bearbeiten" data-testid={`edit-quote-${q.id}`}>
                        <PencilSimple weight="bold" size={16} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setHistoryQuote(q)} className="text-[#525866]" title="Verlauf" data-testid={`history-quote-${q.id}`}>
                        <ClockCounterClockwise weight="bold" size={16} />
                      </Button>
                      {q.status === 'offen' && (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => handleAccept(q.id)} className="text-[#009951]" title="Annehmen" data-testid={`accept-quote-${q.id}`}><Check weight="bold" size={16} /></Button>
                          <Button variant="ghost" size="sm" onClick={() => handleReject(q.id)} className="text-[#E60000]" title="Ablehnen"><X weight="bold" size={16} /></Button>
                        </>
                      )}
                      {(q.status === 'offen' || q.status === 'angenommen') && q.vehicle_id && !q.converted_booking_id && (
                        <Button variant="outline" size="sm" onClick={() => handleConvert(q)} className="text-[#009951] border-[#009951] hover:bg-green-50 ml-1" title="In Buchung umwandeln" data-testid={`convert-quote-${q.id}`}>
                          <ArrowRight weight="bold" size={14} className="mr-1" /> Buchung
                        </Button>
                      )}
                      {q.converted_booking_id && (
                        <Button variant="outline" size="sm" onClick={() => navigate(`/bookings/${q.converted_booking_id}`)} className="text-[#525866] ml-1" title="Zur Buchung">
                          <Eye weight="bold" size={14} className="mr-1" /> Buchung
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(q.id)} className="text-[#E60000] hover:bg-red-50"><Trash weight="bold" size={16} /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Quote Modal */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-xl font-bold">Neues Angebot</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="overline">Kunde *</Label>
                <button type="button" onClick={() => setShowQuickCustomer(true)} className="text-xs text-[#E60000] hover:underline flex items-center gap-1"><Plus weight="bold" size={12} /> Neuer Kunde</button>
              </div>
              <Select value={customerId} onValueChange={setCustomerId}>
                <SelectTrigger className="input-swiss" data-testid="quote-customer"><SelectValue placeholder="Kunde wählen..." /></SelectTrigger>
                <SelectContent>{customers.map(c => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name} {c.email ? `— ${c.email}` : ''}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            {/* Optional: Vehicle rental */}
            <div className="p-4 bg-[#F7F8FA] border border-[#E2E4E9] rounded-sm space-y-3">
              <p className="overline">Fahrzeugmiete (optional)</p>
              <Select value={vehicleId} onValueChange={setVehicleId}>
                <SelectTrigger className="input-swiss"><SelectValue placeholder="Fahrzeug wählen (optional)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Kein Fahrzeug —</SelectItem>
                  {vehicles.map(v => <SelectItem key={v.id} value={v.id}>{v.brand} {v.model} ({v.license_plate}) — €{v.daily_rate}/Tag</SelectItem>)}
                </SelectContent>
              </Select>
              {vehicleId && vehicleId !== 'none' && (
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs text-[#8A94A6]">Von</Label><Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="input-swiss" /></div>
                  <div><Label className="text-xs text-[#8A94A6]">Bis</Label><Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="input-swiss" /></div>
                </div>
              )}
              {vehicleTotal > 0 && <p className="text-sm text-[#525866]">{vehicleDays} Tage × €{selectedVehicle?.daily_rate}/Tag = <b>€{vehicleTotal.toFixed(2)}</b></p>}
            </div>

            {/* Custom line items */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="overline">Weitere Positionen</Label>
                <Button type="button" variant="outline" size="sm" onClick={addLine}><Plus weight="bold" size={14} className="mr-1" /> Position</Button>
              </div>
              {lineItems.map((item, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 mb-2 items-end">
                  <div className="col-span-5">
                    {i === 0 && <Label className="text-xs text-[#8A94A6]">Beschreibung</Label>}
                    <Input value={item.description} onChange={e => updateLine(i, 'description', e.target.value)} className="input-swiss" placeholder="z.B. GPS, Versicherung..." />
                  </div>
                  <div className="col-span-2">
                    {i === 0 && <Label className="text-xs text-[#8A94A6]">Menge</Label>}
                    <Input type="number" value={item.quantity} onChange={e => updateLine(i, 'quantity', parseInt(e.target.value) || 1)} className="input-swiss" min={1} />
                  </div>
                  <div className="col-span-3">
                    {i === 0 && <Label className="text-xs text-[#8A94A6]">Preis (€)</Label>}
                    <Input type="number" value={item.unit_price} onChange={e => updateLine(i, 'unit_price', parseFloat(e.target.value) || 0)} className="input-swiss" min={0} step={0.01} />
                  </div>
                  <div className="col-span-2 flex items-center gap-1">
                    <span className="text-sm font-medium">€{(item.quantity * item.unit_price).toFixed(2)}</span>
                    {lineItems.length > 1 && <Button type="button" variant="ghost" size="sm" onClick={() => removeLine(i)} className="text-[#E60000] p-1"><Trash weight="bold" size={14} /></Button>}
                  </div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div><Label className="overline">Rabatt (€)</Label><Input type="number" value={discount} onChange={e => setDiscount(parseFloat(e.target.value) || 0)} className="input-swiss" min={0} step={0.01} /></div>
              <div><Label className="overline">Gültig bis</Label><Input type="date" value={validUntil} onChange={e => setValidUntil(e.target.value)} className="input-swiss" /></div>
              <div className="flex items-end"><div className="p-3 bg-[#F7F8FA] border border-[#E2E4E9] rounded-sm w-full text-right"><p className="text-xs text-[#8A94A6]">Netto-Schätzung</p><p className="text-xl font-bold text-[#E60000]">€{estTotal.toFixed(2)}</p></div></div>
            </div>

            <div><Label className="overline">Notizen</Label><textarea value={notes} onChange={e => setNotes(e.target.value)} className="input-swiss w-full px-3 py-2" rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Abbrechen</Button>
            <Button className="btn-primary" onClick={handleCreate} disabled={!customerId} data-testid="submit-quote-btn">Angebot erstellen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <QuickCustomerCreate open={showQuickCustomer} onOpenChange={setShowQuickCustomer} onCreated={(c) => { setCustomers(prev => [...prev, c]); setCustomerId(c.id); }} />

      {/* Edit Quote Modal */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-xl font-bold">Angebot bearbeiten {editingQuote?.quote_number ? `· ${editingQuote.quote_number}` : ''}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="overline">Kunde *</Label>
              <Select value={customerId} onValueChange={setCustomerId}>
                <SelectTrigger className="input-swiss"><SelectValue /></SelectTrigger>
                <SelectContent>{customers.map(c => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name} {c.email ? `— ${c.email}` : ''}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            <CustomerOverrideFields
              customer={customers.find((c) => c.id === customerId)}
              override={customerOverride}
              onChange={setCustomerOverride}
              title="Kundendaten (nur für dieses Angebot)"
            />

            <div className="p-4 bg-[#F7F8FA] border border-[#E2E4E9] rounded-sm space-y-3">
              <p className="overline">Fahrzeugmiete</p>
              <Select value={vehicleId || 'none'} onValueChange={setVehicleId}>
                <SelectTrigger className="input-swiss"><SelectValue placeholder="Fahrzeug wählen" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Kein Fahrzeug —</SelectItem>
                  {vehicles.map(v => <SelectItem key={v.id} value={v.id}>{v.brand} {v.model} ({v.license_plate}) — €{v.daily_rate}/Tag</SelectItem>)}
                </SelectContent>
              </Select>
              {vehicleId && vehicleId !== 'none' && (
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs text-[#8A94A6]">Von</Label><Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="input-swiss" /></div>
                  <div><Label className="text-xs text-[#8A94A6]">Bis</Label><Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="input-swiss" /></div>
                </div>
              )}
              {vehicleTotal > 0 && <p className="text-sm text-[#525866]">{vehicleDays} Tage × €{selectedVehicle?.daily_rate}/Tag = <b>€{vehicleTotal.toFixed(2)}</b></p>}
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="overline">Weitere Positionen</Label>
                <Button type="button" variant="outline" size="sm" onClick={addLine}><Plus weight="bold" size={14} className="mr-1" /> Position</Button>
              </div>
              {lineItems.map((item, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 mb-2 items-end">
                  <div className="col-span-5">
                    {i === 0 && <Label className="text-xs text-[#8A94A6]">Beschreibung</Label>}
                    <Input value={item.description} onChange={e => updateLine(i, 'description', e.target.value)} className="input-swiss" />
                  </div>
                  <div className="col-span-2">
                    {i === 0 && <Label className="text-xs text-[#8A94A6]">Menge</Label>}
                    <Input type="number" value={item.quantity} onChange={e => updateLine(i, 'quantity', parseInt(e.target.value) || 1)} className="input-swiss" min={1} />
                  </div>
                  <div className="col-span-3">
                    {i === 0 && <Label className="text-xs text-[#8A94A6]">Preis (€)</Label>}
                    <Input type="number" value={item.unit_price} onChange={e => updateLine(i, 'unit_price', parseFloat(e.target.value) || 0)} className="input-swiss" min={0} step={0.01} />
                  </div>
                  <div className="col-span-2 flex items-center gap-1">
                    <span className="text-sm font-medium">€{(item.quantity * item.unit_price).toFixed(2)}</span>
                    {lineItems.length > 1 && <Button type="button" variant="ghost" size="sm" onClick={() => removeLine(i)} className="text-[#E60000] p-1"><Trash weight="bold" size={14} /></Button>}
                  </div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div><Label className="overline">Rabatt (€)</Label><Input type="number" value={discount} onChange={e => setDiscount(parseFloat(e.target.value) || 0)} className="input-swiss" min={0} step={0.01} /></div>
              <div><Label className="overline">Gültig bis</Label><Input type="date" value={validUntil} onChange={e => setValidUntil(e.target.value)} className="input-swiss" /></div>
              <div className="flex items-end"><div className="p-3 bg-[#F7F8FA] border border-[#E2E4E9] rounded-sm w-full text-right"><p className="text-xs text-[#8A94A6]">Neu (Brutto)</p><p className="text-xl font-bold text-[#E60000]">€{estTotal.toFixed(2)}</p></div></div>
            </div>

            <div><Label className="overline">Notizen</Label><textarea value={notes} onChange={e => setNotes(e.target.value)} className="input-swiss w-full px-3 py-2" rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEdit(false); resetForm(); }}>Abbrechen</Button>
            <Button className="btn-primary" onClick={handleSaveEdit} disabled={!customerId} data-testid="submit-edit-quote-btn">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <RevisionHistory
        open={!!historyQuote}
        onOpenChange={(v) => !v && setHistoryQuote(null)}
        entityType="quote"
        entityId={historyQuote?.id}
        title={historyQuote?.quote_number}
      />
    </div>
  );
};

export default Quotes;
